import { Checkbox, DatePicker, Select, Typography } from 'antd';
import moment from 'moment';
import { IBestDataset, IPlotVariable } from '../../../../../../types/analytics';
import { DateFormat } from '../../../../../../utils/StaticVariable';
import styles from '../../Layout/AnalyticsLayout.module.scss';

const { Text } = Typography;

interface TSPlotInputFormProps {
  datasets: IBestDataset[];
  previewDatasetId: string;
  onDatasetChange: (datasetId: string) => void;
  categories: string[];
  variables: IPlotVariable[];
  tempVariable: IPlotVariable | null;
  onVariableChange: (variableIndex: number) => void;
  startDate: moment.Moment;
  endDate: moment.Moment;
  onStartDateChange: (date: moment.Moment | null) => void;
  onEndDateChange: (date: moment.Moment | null) => void;
  disabledStartDate: (date: moment.Moment) => boolean;
  disabledEndDate: (date: moment.Moment) => boolean;
  isForAllAnalytics: boolean;
  onCheckboxChange: () => void;
  isLoading?: boolean;
  hasError?: boolean;
}

export const TSPlotInputForm = ({
  datasets,
  previewDatasetId,
  onDatasetChange,
  categories,
  variables,
  tempVariable,
  onVariableChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  disabledStartDate,
  disabledEndDate,
  isForAllAnalytics,
  onCheckboxChange,
  isLoading = false,
  hasError = false,
}: TSPlotInputFormProps) => {
  return (
    <div className={styles.formRow}>
      <div className={`${styles.formItem} ${styles.datasetItem}`}>
        <Text>Dataset</Text>
        <Select
          value={previewDatasetId}
          onChange={onDatasetChange}
          placeholder='Select dataset'
          disabled={isLoading}
          id='Analytics-TimeSeries-Dataset_Input'
          data-testid='Analytics-TimeSeries-Dataset_Input'
        >
          {categories.map((category: string) => (
            <Select.OptGroup key={category} label={category}>
              {datasets
                .filter((item) => item.label === category)
                .map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.value}
                  </Select.Option>
                ))}
            </Select.OptGroup>
          ))}
        </Select>
      </div>

      <div className={`${styles.formItem} ${styles.variableItem}`}>
        <Text>Variable</Text>
        <Select
          placeholder='Select variable'
          onChange={onVariableChange}
          value={tempVariable?.Index ? tempVariable.Index : 0}
          disabled={isLoading || hasError}
          id='Analytics-TimeSeries-Variable_Input'
        >
          {variables
            .filter((item) => item.Name !== 'Time')
            .map((item) => (
              <Select.Option key={item.Index} value={item.Index}>
                {item.Name}
              </Select.Option>
            ))}
        </Select>
      </div>

      <div
        className={`${styles.formItem} ${styles.dateItem}`}
        data-testid='Analytics-StartDate_Input'
      >
        <Text>Start Date</Text>
        <DatePicker
          value={moment(startDate)}
          disabledDate={disabledStartDate}
          onChange={onStartDateChange}
          format={DateFormat}
          disabled={isLoading || hasError}
        />
      </div>

      <div
        className={`${styles.formItem} ${styles.dateItem}`}
        data-testid='Analytics-EndDate_Input'
      >
        <Text>End Date</Text>
        <DatePicker
          value={moment(endDate)}
          disabledDate={disabledEndDate}
          onChange={onEndDateChange}
          format={DateFormat}
          disabled={isLoading || hasError}
        />
      </div>

      <div
        className={`${styles.formItem} ${styles.checkboxItem}`}
        data-testid='Analytics-Period-Checkbox'
      >
        <Checkbox
          disabled={hasError || isLoading}
          checked={isForAllAnalytics}
          onChange={onCheckboxChange}
        />
        <Text onClick={onCheckboxChange} style={{ cursor: 'pointer' }}>
          Use this period for all analytics
        </Text>
      </div>
    </div>
  );
};

export type { TSPlotInputFormProps };
